/* FANGZ pack add-to-cart — the only ATC path on flavour PDPs.
   "Add 3-Pack" / "Add 5-Pack" add N of the selected size variant as one
   line (qty N); customers trim individually in the cart. 5+ triggers the
   "15% off 5+ chews" Shopify automatic discount.

   Module (type="module") so it can import the theme's CartAddEvent and let
   the native cart drawer + cart-items components refresh themselves.
   Falls back to a /cart navigation if anything fails or no drawer exists. */
import { CartAddEvent } from '@theme/events';

(function () {
  'use strict';

  // Must match the Shopify automatic discount "15% off 5+ chews".
  // Single source of truth for the PDP pack-price preview.
  var DISCOUNT_QTY = 5;
  var DISCOUNT_PCT = 0.15;

  function money(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  function init(root) {
    if (!root || root.__fzPackInit) return;
    root.__fzPackInit = true;

    var sizeMap = {};
    try { sizeMap = JSON.parse(root.getAttribute('data-variants') || '{}'); } catch (e) { sizeMap = {}; }

    var packButtons = Array.prototype.slice.call(root.querySelectorAll('[data-fz-pack]'));
    if (!packButtons.length) return;

    var inFlight = false;

    function currentSize() {
      var active = root.querySelector('.sizechip.active[data-size-chip]');
      if (active) return active.getAttribute('data-size-chip');
      return root.getAttribute('data-default-size') || 'medium';
    }

    function currentVariant() {
      return sizeMap[currentSize()] || null;
    }

    function renderPrices() {
      var v = currentVariant();
      packButtons.forEach(function (btn) {
        var qty = parseInt(btn.getAttribute('data-fz-pack'), 10) || 1;
        var totalEl = btn.querySelector('[data-fz-pack-total]');
        var unavailable = !v || !v.available || !v.id;
        // Never re-enable a button while an add is in flight, or a size-chip
        // click mid-request would clear the disable and allow a double add.
        btn.disabled = unavailable || inFlight;
        if (!totalEl || !v) return;
        var total = v.price * qty;
        // Subtract round(total x pct) — matches Shopify's allocate-then-round so
        // the preview equals the charged price to the cent.
        if (qty >= DISCOUNT_QTY) total = total - Math.round(total * DISCOUNT_PCT);
        totalEl.textContent = money(total);
      });
    }

    // Recompute pack prices when the size changes. fangz-pdp.js toggles the
    // .active class synchronously on click, so we read it on the next tick.
    root.querySelectorAll('.sizechip[data-size-chip]').forEach(function (chip) {
      chip.addEventListener('click', function () { setTimeout(renderPrices, 0); });
    });

    function cartSectionIds() {
      return Array.prototype.slice
        .call(document.querySelectorAll('cart-items-component'))
        .map(function (c) { return c.dataset.sectionId; })
        .filter(Boolean);
    }

    function cartAddUrl() {
      return (window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url) || '/cart/add';
    }

    function refreshCartUI(qty, data) {
      // Runs only AFTER a successful add. A failure in here must not bounce the
      // customer to /cart, because the item is already in their cart.
      return fetch('/cart.js')
        .then(function (r) { return r.json(); })
        .catch(function () { return null; })
        .then(function (cart) {
          var drawer = document.querySelector('cart-drawer-component');
          if (!drawer) {
            // No drawer on this template: show the full cart page instead.
            window.location.href = '/cart';
            return;
          }
          // Same event the theme's own ATC fires: cart-items-component morphs
          // the cart (items + our progress bar + count) from detail.data.sections,
          // and cart-icon updates its bubble.
          document.dispatchEvent(
            new CartAddEvent(cart || undefined, 'fangz-pack', {
              source: 'fangz-pack',
              itemCount: qty,
              sections: data && data.sections
            })
          );
          // auto_open_cart_drawer is OFF on this theme, so this explicit open()
          // is the ONLY thing that opens the drawer. Do not remove it.
          if (typeof drawer.open === 'function') {
            try { drawer.open(); } catch (e) { /* no-op */ }
          }
        });
    }

    function addPack(qty, btn) {
      if (inFlight) return;
      var v = currentVariant();
      if (!v || !v.available || !v.id) return;

      inFlight = true;
      packButtons.forEach(function (b) { b.disabled = true; });
      btn.classList.add('is-loading');
      btn.setAttribute('aria-busy', 'true');

      var sections = cartSectionIds();

      fetch(cartAddUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          id: v.id,
          quantity: qty,
          sections: sections.join(','),
          sections_url: window.location.pathname
        })
      })
        .then(function (res) { return res.json(); })
        .then(function (data) {
          if (data && data.status) {
            // Shopify returns {status, message, description} when the add fails.
            throw new Error(data.description || data.message || 'Add to cart failed');
          }
          // Own chain so a refresh error never triggers the add-failed fallback.
          return refreshCartUI(qty, data).catch(function (e) {
            console.error('[fangz-pack] post-add refresh failed', e);
          });
        })
        .catch(function (err) {
          // Only reached when the add POST itself failed: don't strand the customer.
          console.error('[fangz-pack]', err);
          window.location.href = '/cart';
        })
        .finally(function () {
          inFlight = false;
          btn.classList.remove('is-loading');
          btn.removeAttribute('aria-busy');
          renderPrices();
        });
    }

    packButtons.forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        var qty = parseInt(btn.getAttribute('data-fz-pack'), 10) || 1;
        addPack(qty, btn);
      });
    });

    renderPrices();
  }

  function boot() {
    document.querySelectorAll('[data-fz-pdp][data-variants]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Re-init on Shopify section reload (theme editor).
  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target.querySelector('[data-fz-pdp]');
    if (root) init(root);
  });
})();
